import * as React from 'react';
import { Grid, Typography, Box } from '@material-ui/core';
import ContentDisplay from '../components/ContentDisplay/ContentDisplay';
import { CostumeEntry } from '../../sprite';
import FolderIcon from '@material-ui/icons/Folder';
import * as Path from 'path';
import TreeView from '@material-ui/lab/TreeView';
import TreeItem from '@material-ui/lab/TreeItem';
import environment from '../../environment';
import { makeStyles } from "@material-ui/core/styles";
import Input from '../components/Input';
import { useTranslation } from 'react-i18next';

const SearchBar: React.FC<{ handleSearchChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void }> = ({
    handleSearchChange,
  }) => {
    const [searchValue, setSearchValue] = React.useState<string>('')
  
    const handleClear = () => {
      setSearchValue('')
      handleSearchChange({ target: { value: '' } } as React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>)
    }
  
    const handleInputChange: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const target = event.target as HTMLInputElement
      setSearchValue(target.value)
      handleSearchChange(event)
    }

    const useSearchBarStyles = makeStyles({
      searchBar: {
        position: 'relative',
      },
      input: {
        transition: 'width 0.3s ease',
        width: (props: { searchValue: string }) => (props.searchValue ? '250px' : '180px'),
        '&:focus-within': {
          width: '250px',
        },
      },
      clearButton: {
        position: 'absolute',
        right: '10px',
        top: '14px',
        cursor: 'pointer',
      },
    });

    const classes = useSearchBarStyles({ searchValue });

    return (
      <Box className={classes.searchBar}>
        <Input
          fullWidth
          placeholder="Suche..."
          value={searchValue}
          onChange={handleInputChange}
          autoComplete="off"
          className={classes.input}
        />
  
        {searchValue && (
          <Box onClick={handleClear} className={classes.clearButton}>
            <Typography variant="body2">X</Typography>
          </Box>
        )}
      </Box>
    );
  }
  

// Define styles for tree view
const useTreeItemStyles = makeStyles((theme) => ({
    root: {
        display: 'inline-flex',
        flexDirection: 'row',
        color: theme.palette.text.secondary,
        '&:hover > $content': {
            backgroundColor: theme.palette.action.hover,
        },
        '&:focus > $content, &$selected > $content': {
            backgroundColor: `var(--tree-view-bg-color, ${theme.palette.grey[400]})`,
            color: 'var(--tree-view-color)',
        },
        '&:focus > $content $label, &:hover > $content $label, &$selected > $content $label': {
            backgroundColor: 'transparent',
        },
    },
    content: {
        color: theme.palette.text.secondary,
        borderTopRightRadius: theme.spacing(2),
        borderBottomRightRadius: theme.spacing(2),
        paddingRight: theme.spacing(1),
        '$expanded > &': {
            fontWeight: theme.typography.fontWeightRegular,
        },
    },
    group: {
        marginLeft: 0,
        '& $content': {
            paddingLeft: theme.spacing(2),
        },
    },
    expanded: {},
    selected: {},
    label: {
        fontWeight: 'inherit',
        color: 'inherit',
    },
    labelRoot: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.5, 0),
    },
    labelIcon: {
        marginRight: theme.spacing(1),
    },
    labelText: {
        fontWeight: 'inherit',
        marginRight: '10px',
    },
}));

//Props for ContentLibrary Tree
interface ContentLibraryTreeItemProps {
    [x: string]: any,

    readonly labelText: string,
    readonly labelIcon: any,
    readonly labelInfo?: string,
    readonly nodeId: any,
}

// Create styled tree view
const ContentLibraryTreeItem: React.FC<ContentLibraryTreeItemProps> = (props) => {
    const classes = useTreeItemStyles();
    const {labelText, labelIcon: LabelIcon, labelInfo, ...other} = props;

    return (
        <TreeItem
            style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
            label={
                <div className={classes.labelRoot}>
                    <LabelIcon color="inherit" className={classes.labelIcon}/>
                    <Typography variant="body2" className={classes.labelText}>
                        {labelText}
                    </Typography>
                    <Typography variant="caption" color="inherit">
                        {labelInfo}
                    </Typography>
                </div>
            }
            classes={{
                root: classes.root,
                content: classes.content,
                expanded: classes.expanded,
                selected: classes.selected,
                group: classes.group,
                label: classes.label,
            }}
            {...other}
        />
    );
};

/***
 * Props for the {@link ContentLibrary} component.
 */
interface ContentLibraryProps {
    /** The parsed content library JSON */
    readonly contentJSON: any;
    /** Called when the user clicked on a media. */
    readonly onMediaSelected: (costumeEntry: CostumeEntry) => void;
}

/***
 * Container for displaying a parsed content JSON.
 */
const ContentLibrary: React.FC<ContentLibraryProps> = (props) => {
    const {t} = useTranslation();
    const [currentPath, setCurrentPath] = React.useState(() => {
        const initialKey = Object.keys(props.contentJSON || {})[0] || '';

        return `/${initialKey === '.' ? '' : initialKey}`;
    });
    const [searchQuery, setSearchQuery] = React.useState('')

    const handleSearchChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };    

    const getFromPath = React.useCallback((path: string = '/') => {
        if (path === '/') {
            return props.contentJSON;
        }
        return [props.contentJSON, ...path.split('/').filter(val => val !== '')].reduce((previousValue, currentValue) => {
            return previousValue[currentValue];
        });
    }, [props.contentJSON]);

    const getAllItems = (obj: any) => {
        let values: any[] = [];

        function traverse(obj: any) {
            for (let key in obj) {
                if (typeof obj[key] === 'object') {
                    traverse(obj[key]);
                } else {
                    values.push(obj[key]);
                }
            }
        }

        traverse(obj);
        return values;
    };

    const setPath = React.useCallback((path: string = '/') => {
        //Do we have an absolute path?
        let newPath;
        if (path.startsWith('/')) {
            newPath = path;
        } else {
            //Else join relative path to current path
            newPath = Path.join(currentPath, path);
        }
        setCurrentPath(newPath);
    }, [currentPath]);

    const formatFolderName = (name: string): string => {
        return name
            .replace(/_/g, ' ')         
            .replace(/\b\w/g, (char) => char.toUpperCase()) 
            .replace(/-/g, ' ');        
    };
    

    /***
     * Function for recursive search in json
     * Returns all renderedItems in a tree view
     */
    const recursiveSearchJson = React.useCallback((obj: object, path: string): any => {
        let items: any[] = [];

        Object.keys(obj).forEach(key => {
            if (key !== '.') {
                const newPath = path + "/" + key;
                const newObj = getFromPath(newPath);
                const formattedName = formatFolderName(key);

                if (Object.keys(newObj).length > 1) {
                    items.push(
                        <ContentLibraryTreeItem
                            key={newPath}
                            nodeId={newPath}
                            labelText={t(`LibraryCostumeModal.treeview.${key.replace(/\//g, '.')}`, formattedName)}
                            labelIcon={FolderIcon}
                            labelInfo=""
                            onClick={() => setPath(newPath)}
                        >
                            {recursiveSearchJson(newObj, newPath)}
                        </ContentLibraryTreeItem>
                    );
                } else {
                    items.push(
                        <ContentLibraryTreeItem
                            key={newPath}
                            nodeId={newPath}
                            labelText={t(`LibraryCostumeModal.treeview.${key.replace(/\//g, '.')}`, formattedName)}
                            labelIcon={FolderIcon}
                            labelInfo=""
                            onClick={() => setPath(newPath)}
                        />
                    );
                }
            }
        });

    return items;
}, [getFromPath, setPath, t]);

    const tree = React.useMemo(() => recursiveSearchJson(props.contentJSON, ""), [props.contentJSON, recursiveSearchJson]);
    const items = React.useMemo(() => getFromPath(currentPath), [currentPath, getFromPath]);
    const allSubItems = getAllItems(items);

    let renderedItems = [];

    if (currentPath.substring(0, currentPath.lastIndexOf("/")) !== "") {
        renderedItems.push(<Grid key={"back"} item={true} xs={6} md={4} lg={2} xl={2}>
            <ContentDisplay
                index={0}
                isActive={false}
                readonly={true}
                costume={new CostumeEntry(t(`LibraryCostumeModal.back`), "./resources/ReplyIcon.svg")}
                onCostumeClicked={() => setPath(currentPath.substring(0, currentPath.lastIndexOf("/")))}
            /></Grid>);
    }
    const allImages = getAllItems(props.contentJSON); // Get all images in the entire library
    const currentImages = getAllItems(items); // Get images from the current path

    const filteredItems = searchQuery.trim() === ''
        ? currentImages // Show current path images when no search query
        : allImages.filter((costumeURL: string) => 
            costumeURL.toLowerCase().includes(searchQuery.toLowerCase())
        ); // Otherwise, filter from all images
        
    const costumes = filteredItems.map((costumeURL: string, index: number) => (
        <Grid key={index} item xs={6} md={4} lg={2} xl={2}>
            <ContentDisplay
                index={index}
                isActive={false}
                readonly={true}
                costume={new CostumeEntry(
                    costumeURL.split('/').pop()?.split('.')[0] || '', 
                    Path.join(environment.CONTENT_FOLDER, costumeURL)
                )}
                onCostumeClicked={(costumeEntry) => props.onMediaSelected && props.onMediaSelected(costumeEntry)}
            />
        </Grid>
    ));


// Use filtered costumes in renderedItems
    renderedItems = searchQuery.length === 0 ? [...renderedItems, ...costumes] : costumes;

    if (allSubItems.length >= 100)  {
        renderedItems = renderedItems.concat((items["."] || [])
            .map((costumeURL: string, index: number) => {
                return (
                    <Grid key={index} item={true} xs={6} md={4} lg={2} xl={2}>
                        <ContentDisplay
                            index={index}
                            isActive={false}
                            readonly={true}
                            costume={new CostumeEntry(costumeURL.split('/').pop()?.split('.')[0] || '', Path.join(environment.CONTENT_FOLDER, costumeURL))}
                            onCostumeClicked={(costumeEntry) => props.onMediaSelected && props.onMediaSelected(costumeEntry)}
                        />
                    </Grid>);
            })
        );
    }

    return (
        <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                    <SearchBar handleSearchChange={handleSearchChange} />
                    <TreeView
                        selected={currentPath}
                        expanded={[]}
                        style={{ display: 'flex', flexDirection: 'row' }}
                    >
                        {tree}
                    </TreeView>
                </div>
            </Grid>
        
            {/* Render Costumes */}
            <Grid item xs style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
                <Grid container spacing={4}>
                    {renderedItems}
                </Grid>
            </Grid>
        </Grid>
    );
};
export default ContentLibrary;
